import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
    const { t } = useI18n();
    const [email, setEmail] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    const [done, setDone] = React.useState(false);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            // Endpoint always returns ok so we don't leak account existence.
            await api.post("/auth/forgot-password", { email });
            setDone(true);
        } catch {
            toast.error(t("forgot_send"));
        } finally { setBusy(false); }
    }

    return (
        <main className="py-16 min-h-[calc(100vh-5rem)] bg-background">
            <div className="max-w-md mx-auto px-6">
                <Card>
                    <CardContent className="p-8">
                        <div className="flex items-center gap-2 mb-2">
                            <KeyRound className="w-5 h-5 text-primary" />
                            <div className="overline text-brand-stone">{t("forgot_kicker")}</div>
                        </div>
                        <h1 className="font-serif text-3xl mb-1">{t("forgot_title")}</h1>
                        {done ? (
                            <div className="mt-6 space-y-4" data-testid="forgot-password-success">
                                <div className="flex items-start gap-2 text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                                    <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                                    <div className="text-sm">{t("forgot_success", { email })}</div>
                                </div>
                                <Link to="/login" className="text-primary underline text-sm">
                                    {t("forgot_back")}
                                </Link>
                            </div>
                        ) : (
                            <>
                                <p className="text-brand-stone text-sm mb-6">{t("forgot_body")}</p>
                                <form onSubmit={submit} className="space-y-4">
                                    <div>
                                        <Label>{t("email")}</Label>
                                        <Input
                                            type="email"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            required
                                            autoFocus
                                            data-testid="forgot-email-input"
                                            className="mt-1.5"
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={busy}
                                        data-testid="send-reset-link-btn"
                                        className="w-full bg-primary hover:bg-primary/90"
                                    >
                                        {busy ? t("forgot_sending") : t("forgot_send")}
                                    </Button>
                                </form>
                                <div className="mt-4 text-sm text-center">
                                    <Link to="/login" className="text-primary underline">{t("forgot_back")}</Link>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </main>
    );
}
